import { format } from "date-fns";
import classNames from "classnames";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLayoutEffect, useMemo, useRef } from "react";
import ConversationMessageGroup from "./ConversationMessageGroup";
import { AlertInCircleIcon, ChatsIcon } from "../fancyIcons";
import NoContent from "../NoContent";

export interface MessageAttachment {
  fileName: string;
  fileObjectKey: string;
}

export interface ThreadMessage {
  chatMessageId: string;
  sentAt: Date;
  Avatar: () => React.ReactNode;
  content: string;
  senderDisplayName: string;
  senderId: string;
  senderIsProvider?: boolean;
  format: "markdown" | "plain" | "html";
  contentColor?: "light" | "normal";
  DisplayComponent?: () => React.ReactNode;
  attachments?: MessageAttachment[];
  className?: string;
}

export const NoConversation = () => {
  return (
    <div className="tw-hidden tw-flex-grow tw-self-stretch md:tw-flex">
      <NoContent Icon={ChatsIcon} label="No Conversations" />
    </div>
  );
};

const Conversation = ({
  messages,
  loadMoreChatHistoryForSelectedConversation,
  hasMoreMessages,
  DisplayComponent,
  error,
}: {
  messages: ThreadMessage[];
  loadMoreChatHistoryForSelectedConversation: () => void;
  hasMoreMessages: boolean;
  DisplayComponent?: () => React.ReactNode;
  error?: Error | null;
}) => {
  const messagesGroupedByDay = useMemo(() => {
    return (messages || []).reduce(
      (acc, message) => {
        const dayKey = format(message.sentAt, "MMM d, yyyy");
        if (!acc[dayKey]) {
          acc[dayKey] = [];
        }

        acc[dayKey].push(message);
        return acc;
      },
      {} as { [key: string]: ThreadMessage[] },
    );
  }, [messages]);

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // If the first page of loaded messages is smaller than the scroll container, we'll never "scroll" back
  // to the top of the container, so we need to load more messages manually to fill the container.
  useLayoutEffect(() => {
    if (messages.length && messagesContainerRef.current && hasMoreMessages) {
      const insideHeight = messagesContainerRef.current.querySelector(
        ".infinite-scroll-component",
      )?.scrollHeight;

      if (
        insideHeight &&
        insideHeight < messagesContainerRef.current.clientHeight
      ) {
        loadMoreChatHistoryForSelectedConversation();
      }
    }
  }, [messagesContainerRef, loadMoreChatHistoryForSelectedConversation]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={styles.conversationContainer}
      id="conversation-scroll-container"
      ref={messagesContainerRef}
    >
      <InfiniteScroll
        dataLength={messages.length}
        next={loadMoreChatHistoryForSelectedConversation}
        hasMore={hasMoreMessages}
        inverse={true}
        loader={
          <div className={styles.lazyLoadingMessageContainer}>
            Loading prior messages...
          </div>
        }
        className={styles.scrollContainer}
        scrollableTarget="conversation-scroll-container"
      >
        {error && (
          <div className={styles.errorMessage}>
            <AlertInCircleIcon />
            {error.message}
          </div>
        )}
        {DisplayComponent && <DisplayComponent />}
        {Object.entries(messagesGroupedByDay)
          .map(([day, messagesInDay]) => (
            <ConversationMessageGroup
              key={day}
              messagesInDay={messagesInDay}
              date={day}
            />
          ))
          .toReversed()}
      </InfiniteScroll>
    </div>
  );
};

const styles = {
  conversationContainer: classNames(
    "tw-flex tw-flex-col-reverse",
    "tw-w-full",
    "tw-grow",
    "tw-overflow-y-auto",
    "tw-px-3 sm:tw-pl-8",
  ),
  scrollContainer: classNames(
    "tw-mt-14",
    "tw-flex tw-flex-col-reverse",
    "md:tw-mt-32",
  ),
  lazyLoadingMessageContainer: classNames(
    "tw-flex",
    "tw-justify-center",
    "tw-p-3",
    "tw-text-sm",
    "tw-text-neutral-400",
  ),
  errorMessage: classNames(
    "tw-flex",
    "tw-items-center",
    "tw-gap-2",
    "tw-text-red-500",
    "tw-text-sm",
    "tw-font-medium",
    "tw-fill-red-500",
    "tw-stroke-red-500",
    "tw-my-4",
  ),
};

export default Conversation;
